import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import RichTextEditor from './RichTextEditor';

export interface HoldReasonOption {
  value: string;
  label: string;
}

/** Stable default so optional prop does not create a new [] every render. */
const EMPTY_HOLD_REASON_OPTIONS: HoldReasonOption[] = [];

interface OnHoldReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmButtonClass?: string;
  isLoading?: boolean;
  fieldLabel?: string;
  placeholder?: string;
  errorMessage?: string;
  isOptional?: boolean;
  holdReasonOptions?: HoldReasonOption[];
}

const OnHoldReasonModal: React.FC<OnHoldReasonModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmButtonClass = 'bg-yellow-600 hover:bg-yellow-700 text-white',
  isLoading = false,
  fieldLabel = 'On-Hold Reason',
  placeholder = 'Enter the reason for putting this incident on hold...',
  errorMessage = 'On-hold reason is required',
  isOptional = false,
  holdReasonOptions = EMPTY_HOLD_REASON_OPTIONS,
}) => {
  const [reason, setReason] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const [error, setError] = useState('');
  const useServiceNowChoices = holdReasonOptions.length > 0;

  // Only reset when the modal opens — do not depend on holdReasonOptions.
  // A new [] reference each render (default prop / parent query default) was
  // clearing the reason while the user typed.
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setSelectedValue('');
      setError('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (useServiceNowChoices) {
      if (!selectedValue) {
        setError(errorMessage);
        return;
      }
      onConfirm(selectedValue);
      return;
    }

    if (!isOptional && !reason.trim()) {
      setError(errorMessage);
      return;
    }
    onConfirm(reason);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                        {title}
                      </Dialog.Title>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                    disabled={isLoading}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-2 ml-9">
                  <p className="text-sm text-gray-500 mb-4">
                    {message}
                  </p>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {useServiceNowChoices ? 'On Hold Reason' : fieldLabel}{' '}
                      {!isOptional && <span className="text-red-500">*</span>}
                    </label>
                    {useServiceNowChoices ? (
                      <>
                        <select
                          className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                            error ? 'border-red-300' : 'border-gray-300'
                          }`}
                          value={selectedValue}
                          onChange={(event) => {
                            setSelectedValue(event.target.value);
                            setError('');
                          }}
                          disabled={isLoading}
                        >
                          <option value="">Select on hold reason...</option>
                          {holdReasonOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          Options are loaded from ServiceNow and will sync to the linked incident.
                        </p>
                      </>
                    ) : (
                      <RichTextEditor
                        value={reason}
                        onChange={(value) => {
                          setReason(value);
                          setError('');
                        }}
                        placeholder={placeholder}
                        minHeight="200px"
                        disabled={isLoading}
                        className={error ? 'border-red-300' : ''}
                      />
                    )}
                    {error && (
                      <p className="mt-1 text-sm text-red-600">{error}</p>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors"
                    disabled={isLoading}
                  >
                    {cancelLabel}
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    className={`px-4 py-2 text-sm font-medium border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 transition-colors ${confirmButtonClass}`}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : confirmLabel}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default OnHoldReasonModal;
